import { Helper } from "../common/helper";
import { Requester } from "../common/requester";
import { Txt } from "../common/text";
import { TimesheetShortcutsSet } from "./timesheet-shortcuts-set";
import { ConfirmationDialogHelper } from "../common/confirmation-dialog-helper";
import {
    TimesheetButtonRequest,
    TimesheetCellButton,
    TimesheetCellData, TimesheetCopyData,
    TimesheetRightTableColumn,
    TimesheetRightTableRowData,
    TimesheetSaveData,
    TimesheetSaveRequest,
    TimesheetUpdateData
} from "./timesheet-models";
import { TimesheetSelectedCells } from "./timesheet-selected-cells";
import { MessageDisplayer } from "../common/message-displayer";
import { Form, FormDisplayer } from "../forms/form";
import { Loader } from "../common/loader";

export class TimesheetTableData
{
    readonly generalUrlParameters: { [key: string]: string };
    readonly saveUrl: string;
    readonly refreshRowDataUrl: string;
    private readonly rows: TimesheetRightTableRowData[] = [];

    constructor(generalUrlParameters: { [key: string]: string }, saveUrl: string, refreshRowDataUrl: string)
    {
        this.generalUrlParameters = generalUrlParameters;
        this.saveUrl = saveUrl;
        this.refreshRowDataUrl = refreshRowDataUrl;
    }

    getRow(rowNumber: number): TimesheetRightTableRowData
    {
        return this.rows[rowNumber];
    }

    addRow(row: TimesheetRightTableRowData): void
    {
        this.rows.push(row);
    }

    getAllRows(): TimesheetRightTableRowData[]
    {
        return this.rows;
    }

    getCell(rowNumber: number, columnNumber: number): TimesheetCellData
    {
        return this.getRow(rowNumber).Cells[columnNumber];
    }
}

export class TimesheetChange
{
    readonly rowNumber: number;
    readonly columnNumber: number;
    readonly oldValue: string;
    readonly newValue: string;

    constructor(rowNumber: number, columnNumber: number, oldValue: string, newValue: string)
    {
        this.rowNumber = rowNumber;
        this.columnNumber = columnNumber;
        this.oldValue = oldValue;
        this.newValue = newValue;
    }
}

export class TimesheetRightTableDataManager
{
    private readonly columns: TimesheetRightTableColumn[];
    private readonly rows: string[];
    private readonly data: TimesheetTableData;
    private readonly shortcuts: TimesheetShortcutsSet;
    private readonly onChange: (updateData: TimesheetUpdateData) => void;

    constructor(columns: TimesheetRightTableColumn[], rows: string[], shortcuts: TimesheetShortcutsSet, data: TimesheetTableData,
                onChange: (updateData: TimesheetUpdateData) => void)
    {
        this.columns = columns;
        this.rows = rows;
        this.shortcuts = shortcuts;
        this.data = data;
        this.onChange = onChange;
    }

    getData()
    {
        return this.data;
    }

    setCellData(rowNumber: number, columnNumber: number, value: TimesheetCellData)
    {
        this.data.getRow(rowNumber).Cells[columnNumber] = value;
    }

    isCellEditable(rowNumber: number, columnNumber: number): boolean
    {
        return this.data.getCell(rowNumber, columnNumber).IsEditable;
    }

    getButtons(rowNumber: number, columnNumber: number): TimesheetCellButton[]
    {
        return this.data.getCell(rowNumber, columnNumber).Buttons.concat(this.data.getRow(rowNumber).Buttons.map(x =>
            x.OpenForm ? { ...x, Url: Requester.getUrl(x.Url, { date: this.columns[columnNumber].Date }) } : x));
    }

    save(changes: TimesheetChange[], restoreData: (change: TimesheetChange) => void)
    {
        const data: TimesheetChange[] = [];
        const saveData: TimesheetSaveData[] = [];
        const changedRows = new Set<number>();
        let smallestColumnId = this.columns.length;
        let largestColumnId = 0;
        let showWarning = false;
        for (const change of changes)
        {
            if (change.newValue === change.oldValue)
            {
                continue;
            }
            const saveInfo = this.tryParseAndSetErrors(change);
            if (!saveInfo)
            {
                restoreData(change);
                continue;
            }
            data.push(change);
            saveData.push(saveInfo);
            changedRows.add(change.rowNumber);
            if (change.columnNumber < smallestColumnId)
            {
                smallestColumnId = change.columnNumber;
            }
            if (change.columnNumber > largestColumnId)
            {
                largestColumnId = change.columnNumber;
            }
            if (!showWarning && this.data.getCell(change.rowNumber, change.columnNumber).ShowWarningOnEdit)
            {
                showWarning = true;
            }
        }
        if (!data.length)
        {
            return;
        }

        const saveRequest: TimesheetSaveRequest = {
            saveInfo: saveData,
            updateInfo: {
                rows: this.getRowsToUpdateCore(Array.from(changedRows)),
                startDate: this.columns[smallestColumnId].Date,
                endDate: this.columns[largestColumnId].Date
            }
        };
        if (showWarning)
        {
            ConfirmationDialogHelper.askConfirmation(Txt.timesheetEditWarning,
                () => this.doSaveRequest(saveRequest),
                () =>
                {
                    for (const change of data)
                    {
                        this.data.getCell(change.rowNumber, change.columnNumber).Error = null;
                        restoreData(change);
                    }
                });
        }
        else
        {
            this.doSaveRequest(saveRequest);
        }
    }

    async copy(data: string[][][], selectedCells: TimesheetSelectedCells)
    {
        try
        {
            Loader.startGlobally();
            const copyInfo: TimesheetCopyData[] = [];
            let showWarning = false;
            const endRow = Math.min(Math.max(selectedCells.startRow + data.length - 1, selectedCells.endRow), this.rows.length - 1);
            const endColumn =
                Math.min(Math.max(selectedCells.startColumn + data[0].length - 1, selectedCells.endColumn), this.columns.length - 1);
            for (let r = selectedCells.startRow; r <= endRow; r++)
            {
                const rowData = data[(r - selectedCells.startRow) % data.length];
                for (let c = selectedCells.startColumn; c <= endColumn; c++)
                {
                    if (!showWarning && this.data.getCell(r, c).ShowWarningOnEdit)
                    {
                        showWarning = true;
                    }
                    copyInfo.push({
                        date: this.columns[c].Date,
                        row: this.rows[r],
                        copyData: rowData[(c - selectedCells.startColumn) % rowData.length]
                    });
                }
            }
            const saveRequest: TimesheetSaveRequest = {
                copyInfo: copyInfo,
                updateInfo: {
                    rows: this.getRowsToUpdate(selectedCells.startRow, endRow),
                    startDate: this.columns[selectedCells.startColumn].Date,
                    endDate: this.columns[endColumn].Date
                }
            };
            if (showWarning)
            {
                ConfirmationDialogHelper.askConfirmation(Txt.timesheetEditWarning,
                    () => this.doSaveRequest(saveRequest));
            }
            else
            {
                await this.doSaveRequest(saveRequest);
            }
        }
        finally
        {
            Loader.stopGlobally();
        }
    }

    async doButtonAction(button: TimesheetCellButton, selectedCells: TimesheetSelectedCells)
    {
        if (button.OpenForm)
        {
            const form = new Form(button.Url);
            form.onDataChanged = () => this.reloadRowData(selectedCells.startRow, selectedCells.endRow);
            FormDisplayer.openInModal(form);
            return;
        }
        try
        {
            Loader.startGlobally();
            const ids: { [rowId: string]: { [date: string]: string[] } } = {};
            if (selectedCells.isSingleCell() && button.GroupButtonId)
            {
                ids[this.rows[selectedCells.startRow]] = {}
                ids[this.rows[selectedCells.startRow]][this.columns[selectedCells.startColumn].Date] = [button.GroupButtonId];
            }
            else
            {
                for (let r = selectedCells.startRow; r <= selectedCells.endRow; r++)
                {
                    ids[this.rows[r]] = {}
                    for (let c = selectedCells.startColumn; c <= selectedCells.endColumn; c++)
                    {
                        ids[this.rows[r]][this.columns[c].Date] = [];
                        for (const x of this.getButtons(r, c))
                        {
                            if (button.Url === x.Url && x.GroupButtonId)
                            {
                                ids[this.rows[r]][this.columns[c].Date].push(x.GroupButtonId);
                            }
                            for (const y of x.SubButtons)
                            {
                                if (button.Url === y.Url && y.GroupButtonId)
                                {
                                    ids[this.rows[r]][this.columns[c].Date].push(y.GroupButtonId);
                                }
                            }
                        }
                    }
                }
            }
            const request: TimesheetButtonRequest =
                    {
                        idsByRowAndDate: ids,
                        rowsToUpdate: this.getRowsToUpdate(selectedCells.startRow, selectedCells.endRow)
                    };
            let updateData: TimesheetUpdateData;
            try
            {
                updateData = await Requester.post(Requester.getUrl(button.Url, this.data.generalUrlParameters), request);
            }
            catch (error)
            {
                MessageDisplayer.showErrorFromServer(error);
            }
            if (updateData)
            {
                this.onChange(updateData);
            }
        }
        finally
        {
            Loader.stopGlobally();
        }
    }

    async reloadRowData(startRow: number, endRow: number): Promise<void>
    {
        const updateData = <TimesheetUpdateData> await Requester.post(this.data.refreshRowDataUrl, this.getRowsToUpdate(startRow, endRow));
        this.onChange(updateData);
    }

    private getRowsToUpdate(startRow: number, endRow: number): string[]
    {
        const changedRows: number[] = [];
        for (let rowNumber = startRow; rowNumber <= endRow; rowNumber++)
        {
            changedRows.push(rowNumber);
        }
        return this.getRowsToUpdateCore(changedRows);
    }

    private getRowsToUpdateCore(changedRows: number[]): string[]
    {
        const changedEmployees = new Set();
        for (const rowNumber of changedRows)
        {
            changedEmployees.add(this.data.getRow(rowNumber).EmployeeId);
        }
        const rowsToUpdate: string[] = [];
        for (let i = 0; i < this.rows.length; i++)
        {
            if (changedEmployees.has(this.data.getRow(i).EmployeeId))
            {
                rowsToUpdate.push(this.rows[i]);
            }
        }
        return rowsToUpdate;
    }

    private async doSaveRequest(saveRequest: TimesheetSaveRequest)
    {
        let updateData: TimesheetUpdateData;
        try
        {
            updateData = await Requester.post(this.data.saveUrl, saveRequest);
        }
        catch (err)
        {
            MessageDisplayer.showErrorFromServer(err);
        }
        this.onChange(updateData);
    }

    private tryParseAndSetErrors(change: TimesheetChange): TimesheetSaveData
    {
        const value = change.newValue ?? "";
        let shortcut: string = null;
        let hours: number = null;
        let startTime: string = null;
        let endTime: string = null;
        const row = this.data.getRow(change.rowNumber);
        const shortcutItem = this.shortcuts.tryGetByAbbreviation(value);
        if (shortcutItem)
        {
            if (!row.AllowedShortcutTypes.includes(shortcutItem.Type))
            {
                row.Cells[change.columnNumber].Error = Txt.cannotInsertThisShortcutToThisRowInTimesheet;
                return null;
            }
            shortcut = shortcutItem.Id;
        }
        else if (Helper.isFloat(value) && Helper.parseFloat(value) <= 24)
        {
            if (!row.AllowHours)
            {
                row.Cells[change.columnNumber].Error = Txt.cannotInsertWorkToThisRowInTimesheet;
                return null;
            }
            hours = Helper.parseFloat(value);
        }
        else if (value.indexOf("-") !== -1)
        {
            const parts = value.split("-");
            const tryParseTimeNumber = (timeToParse: string, parseMinutes: boolean = false): string =>
            {
                if (timeToParse.length === 1 && !parseMinutes || timeToParse.length === 2)
                {
                    const parsedNumber = parseInt(timeToParse);
                    if (parsedNumber < (parseMinutes ? 60 : 24) && parsedNumber >= 0)
                    {
                        return parsedNumber < 10 ? "0" + parsedNumber.toString() : parsedNumber.toString();
                    }
                }
                return null;
            };
            const tryParseTime = (timeToParse: string): string =>
            {
                if (timeToParse.length === 1 || timeToParse.length === 2)
                {
                    const parsed = tryParseTimeNumber(timeToParse);
                    return parsed === null ? null : parsed + ":00";
                }
                const timeParts = timeToParse.split(":");
                if (timeParts.length === 2)
                {
                    const hoursPart = tryParseTimeNumber(timeParts[0]);
                    const minutesPart = tryParseTimeNumber(timeParts[1], true);
                    return hoursPart === null || minutesPart === null ? null : hoursPart + ":" + minutesPart;
                }
                return null;
            };
            if (parts.length === 2)
            {
                startTime = tryParseTime(parts[0]);
                endTime = tryParseTime(parts[1]);
                if (startTime === null || endTime === null)
                {
                    row.Cells[change.columnNumber].Error = Txt.invalidValueInsertedInTimesheet;
                    return null;
                }
            }
        }
        else
        {
            if (value !== "")
            {
                row.Cells[change.columnNumber].Error = Txt.invalidValueInsertedInTimesheet;
                return null;
            }
        }
        return {
            date: this.columns[change.columnNumber].Date,
            row: this.rows[change.rowNumber],
            shortcut: shortcut,
            hours: hours,
            startTime: startTime,
            endTime: endTime
        };
    }
}
